import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';
import { RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';
import { navigateToUserProfileWithPerson } from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { hasValue, hasStringValue, currentUserHasPermission } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { b64toBlob } from '@/utils/blob';

// ── Global Declarations ──────────────────────────────────────────────

import { isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp, notifyApp } from '@/utils/window-globals';

import { getCurrentUserId, getAuthObject, setAuthObject } from '@/utils/auth';
import { saveAs } from 'file-saver';

interface Attachment {
  id: number;
  signedURL: string;
}

interface AttachmentInfo {
  id: number;
  activationKey: string | null;
}

// ── Props ────────────────────────────────────────────────────────────

export interface UserProfileScreenProps {
  userId: string;
}

// ── Component ────────────────────────────────────────────────────────

export function UserProfileScreen({ userId }: UserProfileScreenProps) {
  const { theme } = useTheme();
  const profilePicInputRef = useRef<HTMLInputElement>(null);

  // Profile data
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCurrentUserProfile] = useState(getCurrentUserId().toString() === userId.toString());
  const [hasNotesAccess] = useState(currentUserHasPermission(AccessAuthorities.ROLE_USER_PROFILE_NOTES));
  const [hasViewOtherProfilesAccess] = useState(currentUserHasPermission(AccessAuthorities.ROLE_VIEW_OTHER_USER_PROFILES));
  const [hasEditOtherProfilesAccess] = useState(currentUserHasPermission(AccessAuthorities.ROLE_EDIT_OTHER_USER_PROFILES));

  // Call sign list
  const [availableCallSigns, setAvailableCallSigns] = useState<any[]>([]);
  const [hasCallSignList, setHasCallSignList] = useState(false);
  const [isCallSignInvalid, setIsCallSignInvalid] = useState(false);

  // User selector
  const [selectedUserId, setSelectedUserId] = useState(parseInt(userId));
  const [peopleInCompany, setPeopleInCompany] = useState<Array<{ id: number; name: string }>>([]);

  // Profile picture
  const [profilePicSrc, setProfilePicSrc] = useState('');

  // Hire date
  const [localHireDate, setLocalHireDate] = useState<Date | null>(null);

  // Notes
  const [notesContent, setNotesContent] = useState('');
  const [notesToolbarConfig] = useState<any>(
    App.isMobile() ? 'mobile' : [
      'formatting', 'fontName', 'fontSize',
      'bold', 'italic', 'underline', 'strikethrough',
      'backColor', 'foreColor', 'separator',
      'insertOrderedList', 'insertUnorderedList', 'indent', 'outdent',
      'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull',
      'lineHeight', 'separator',
      'createLink', 'unlink', 'insertImage', 'uploadImage', 'insertFile', 'separator',
      'insertUpperRomanList', 'insertLowerRomanList',
      'createTable',
      'addRowAbove', 'addRowBelow', 'addColumnLeft', 'addColumnRight',
      'deleteRow', 'deleteColumn',
      'mergeCellsHorizontally', 'mergeCellsVertically',
      'splitCellHorizontally', 'splitCellVertically',
      'tableAlignLeft', 'tableAlignCenter', 'tableAlignRight', 'separator',
      'undo', 'redo', 'cleanFormatting', 'copyFormat', 'applyFormat', 'separator',
      'print', 'exportPdf',
    ]
  );
  const editorAttachmentKeysRef = useRef<Record<number, string>>({});
  const notesEditorRef = useRef<RichTextEditorHandle>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences'>('profile');

  // Preferences tab state
  const [userSettings, setUserSettings] = useState<any>(null);
  const [isPreferencesEditMode, setIsPreferencesEditMode] = useState(false);
  const [preferencesFormData, setPreferencesFormData] = useState<any>(null);

  // Modals
  const [changePasswordModal, setChangePasswordModal] = useState({
    open: false,
    currentPassword: '',
    newPassword1: '',
    newPassword2: '',
  });
  const [resetPasswordModal, setResetPasswordModal] = useState({ open: false });
  const [exportPdfHelpOpen, setExportPdfHelpOpen] = useState(false);

  // ── Computed ─────────────────────────────────────────────────────

  const computedIsCallSignInvalid = useMemo(() => {
    if (!hasCallSignList) return false;
    if (!hasStringValue(userProfile?.callSign)) return false;
    return !availableCallSigns.some((cs: any) => cs.callSign === userProfile?.callSign);
  }, [hasCallSignList, userProfile?.callSign, availableCallSigns]);

  const hasProfilePic = useMemo(() => {
    return hasValue(userProfile?.profilePic) && hasStringValue(profilePicSrc);
  }, [userProfile?.profilePic, profilePicSrc]);

  const userInitials = useMemo(() => {
    const first = userProfile?.firstName || '';
    const last = userProfile?.lastName || '';
    const firstInitial = first.charAt(0).toUpperCase();
    const lastInitial = last.charAt(0).toUpperCase();
    return (firstInitial + lastInitial) || '?';
  }, [userProfile?.firstName, userProfile?.lastName]);

  const avatarColor = useMemo(() => {
    const colors = [
      '#1d3c59', '#2e7d32', '#c62828', '#6a1b9a',
      '#00838f', '#ef6c00', '#4527a0', '#00695c',
      '#d84315', '#1565c0', '#ad1457', '#37474f',
    ];
    const name = (userProfile?.firstName || '') + (userProfile?.lastName || '');
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }, [userProfile?.firstName, userProfile?.lastName]);

  const callSignOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = [];

    if (isCallSignInvalid && hasStringValue(userProfile?.callSign)) {
      options.push({
        label: `${userProfile.callSign} (no longer available)`,
        value: userProfile.callSign,
      });
    }

    availableCallSigns.forEach((cs: any) => {
      options.push({ label: cs.callSign, value: cs.callSign });
    });

    return options;
  }, [isCallSignInvalid, userProfile?.callSign, availableCallSigns]);

  // ── Load Notes Images ────────────────────────────────────────────

  const loadNotesImages = useCallback((content: string, callback: (updated: string) => void) => {
    if (!content || !content.includes('data-attachment-id')) {
      return;
    }

    api.listUserProfileNoteAttachments(parseInt(userId), (result: any) => {
      const attachments = result.attachmentList;
      if (!attachments || attachments.length === 0) return;

      const urlMap: Record<number, string> = {};
      attachments.forEach((a: any) => {
        urlMap[a.id] = a.signedURL;
      });

      let updated = content;
      const imgRegex = /<img([^>]*?)data-attachment-id="(\d+)"([^>]*?)src="[^"]*"([^>]*?)>/g;
      updated = updated.replace(imgRegex, (_match: string, before: string, id: string, middle: string, after: string) => {
        const freshUrl = urlMap[parseInt(id)];
        if (freshUrl) return `<img${before}data-attachment-id="${id}"${middle}src="${freshUrl}"${after}>`;
        return _match;
      });

      const imgRegex2 = /<img([^>]*?)src="[^"]*"([^>]*?)data-attachment-id="(\d+)"([^>]*?)>/g;
      updated = updated.replace(imgRegex2, (_match: string, before: string, middle: string, id: string, after: string) => {
        const freshUrl = urlMap[parseInt(id)];
        if (freshUrl) return `<img${before}src="${freshUrl}"${middle}data-attachment-id="${id}"${after}>`;
        return _match;
      });

      callback(updated);
    }, () => {
      // Error loading attachments - silently fail
    });
  }, [userId]);

  // ── Load User Settings (Preferences tab) ────────────────────────

  useEffect(() => {
    api.getUserSettings(userId, (result: any) => {
      if (result && result.settings) {
        setUserSettings(result.settings);
        setPreferencesFormData({ ...result.settings });
      }
    }, () => {
      // Settings load failed - leave as null
    });
  }, [userId]);

  // ── Preferences Actions ────────────────────────────────────────

  const handlePreferencesEdit = useCallback(() => {
    setPreferencesFormData({ ...userSettings });
    setIsPreferencesEditMode(true);
  }, [userSettings]);

  const handlePreferencesUpdate = useCallback(() => {
    api.updateUserSettings(userId, {
      isMdtCallCardEditModeDefault: preferencesFormData.isMdtCallCardEditModeDefault,
    }, () => {
      setUserSettings({ ...preferencesFormData });
      setIsPreferencesEditMode(false);
      if (isCurrentUserProfile) {
        const authObj = getAuthObject();
        if (authObj) {
          if (!authObj.personSettings) authObj.personSettings = {};
          authObj.personSettings.isMdtCallCardEditModeDefault = preferencesFormData.isMdtCallCardEditModeDefault;
          setAuthObject(authObj);
        }
        App.showPopupNotification('Preferences updated successfully.', 'success');
      } else {
        App.showPopupNotification('Preferences updated successfully. Changes take effect on their next login.', 'success');
      }
    }, () => {
      App.showPopupNotification('Failed to update preferences.', 'error');
    });
  }, [userId, preferencesFormData]);

  const handlePreferencesCancel = useCallback(() => {
    setPreferencesFormData({ ...userSettings });
    setIsPreferencesEditMode(false);
  }, [userSettings]);

  // ── Load Profile ─────────────────────────────────────────────────

  useEffect(() => {
    api.getUserProfile(userId, (result: any) => {
      // Build profile object by creating a simple copy
      const profile: any = {};
      for (const k in result) {
        if (result.hasOwnProperty(k)) {
          profile[k] = result[k];
        }
      }
      if (hasValue(profile.profilePic)) {
        profile.profilePicInfo = { id: profile.profilePic.id, activationKey: null };
      }

      const callSigns = result.availableCallSigns || [];
      const hasList = hasValue(result.availableCallSigns) && result.availableCallSigns.length > 0;
      const currentCS = profile.callSign;
      const csInvalid = hasList && hasStringValue(currentCS) && !callSigns.some((cs: any) => cs.callSign === currentCS);

      let people: Array<{ id: number; name: string }> = result.peopleInCompany || [];
      people.sort((a, b) => a.name.localeCompare(b.name));

      setUserProfile(profile);
      setAvailableCallSigns(callSigns);
      setHasCallSignList(hasList);
      setIsCallSignInvalid(csInvalid);
      setPeopleInCompany(people);
      setSelectedUserId(parseInt(userId));
      setProfilePicSrc(hasValue(profile.profilePic) ? profile.profilePic.signedURL : '');
      setLocalHireDate(hasValue(profile.hireDate) ? new Date(profile.hireDate) : null);

      const notes = result.notes || '';
      if (hasNotesAccess && notes && notes.includes('data-attachment-id')) {
        loadNotesImages(notes, (updated) => {
          setNotesContent(updated);
        });
      } else {
        setNotesContent(notes);
      }
    });
  }, [userId, hasNotesAccess, loadNotesImages]);

  // ── Profile Update Field Helper ──────────────────────────────────

  const updateProfileField = useCallback((field: string, value: any) => {
    setUserProfile((prev: any) => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  }, []);

  // ── Actions ──────────────────────────────────────────────────────

  const handleEdit = useCallback(() => setIsEditMode(true), []);

  const handleUpdate = useCallback(() => {
    if (hasCallSignList && hasStringValue(userProfile?.callSign)) {
      const isValid = availableCallSigns.some((cs: any) => cs.callSign === userProfile.callSign);
      if (!isValid) {
        App.showPopupNotification('Please select a valid call sign from the list.', 'error');
        return;
      }
    }

    // Sync hire date
    userProfile.hireDate = localHireDate;

    // Sync notes content
    if (hasNotesAccess) {
      const content = notesContent;
      const attachmentInfoList: any[] = [];
      const imgRegex = /data-attachment-id="(\d+)"/g;
      let match;
      while ((match = imgRegex.exec(content)) !== null) {
        const id = parseInt(match[1]);
        attachmentInfoList.push({
          id,
          activationKey: editorAttachmentKeysRef.current[id] || null,
        });
      }
      userProfile.notes = content;
      userProfile.notesAttachmentInfoList = attachmentInfoList;
    }

    api.updateUserProfile(userId, userProfile, () => {
      setIsEditMode(false);
      setIsCallSignInvalid(computedIsCallSignInvalid);
      App.showPopupNotification('Profile updated successfully.', 'success');
    });
  }, [userProfile, hasCallSignList, availableCallSigns, localHireDate, hasNotesAccess, notesContent, userId, computedIsCallSignInvalid]);

  const handleCancel = useCallback(() => {
    setIsEditMode(false);

    api.getUserProfile(userId, (result: any) => {
      if (result == null) return;

      const profile = { ...userProfile };
      for (const k in result) {
        if (profile[k] !== undefined && result[k] !== undefined) {
          profile[k] = result[k];
        }
      }

      if (hasValue(result.profilePic)) {
        setProfilePicSrc(result.profilePic.signedURL);
        profile.profilePic = result.profilePic;
      } else {
        setProfilePicSrc('');
        profile.profilePic = null;
      }

      setUserProfile(profile);
      setLocalHireDate(hasValue(result.hireDate) ? new Date(result.hireDate) : null);

      const notes = result.notes || '';
      if (hasNotesAccess && notes && notes.includes('data-attachment-id')) {
        loadNotesImages(notes, (updated) => setNotesContent(updated));
      } else {
        setNotesContent(notes);
      }

      const csInvalid = hasCallSignList &&
        hasStringValue(result.callSign) &&
        !availableCallSigns.some((cs: any) => cs.callSign === result.callSign);
      setIsCallSignInvalid(csInvalid);
    });
  }, [userId, userProfile, hasNotesAccess, hasCallSignList, availableCallSigns, loadNotesImages]);

  const onUserSelect = useCallback((personId: number) => {
    if (personId === selectedUserId) return;
    navigateToUserProfileWithPerson(personId);
  }, [selectedUserId]);

  const onCallSignSelect = useCallback((value: any) => {
    updateProfileField('callSign', value === '' ? null : value);
    // Update invalid state
    if (!hasCallSignList) {
      setIsCallSignInvalid(false);
    } else if (!hasStringValue(value === '' ? null : value)) {
      setIsCallSignInvalid(false);
    } else {
      setIsCallSignInvalid(!availableCallSigns.some((cs: any) => cs.callSign === value));
    }
  }, [updateProfileField, hasCallSignList, availableCallSigns]);

  // Profile picture
  const triggerProfilePicUpload = useCallback(() => {
    if (!isEditMode) return;
    profilePicInputRef.current?.click();
  }, [isEditMode]);

  const onProfilePicChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) return;

    App.uploadFromFileInput(fileInput, {
      successCallback: (_idx: number, attachment: Attachment) => {
        updateProfileField('profilePic', attachment);
        setProfilePicSrc(attachment.signedURL);
      },
      updateAttachmentInfo: (_idx: number, attachmentInfo: AttachmentInfo) => {
        updateProfileField('profilePicInfo', attachmentInfo);
      },
      updateAttachment: (_idx: number, attachment: Attachment) => {
        updateProfileField('profilePic', attachment);
      },
    });

    fileInput.value = '';
  }, [updateProfileField]);

  const removeProfilePic = useCallback(() => {
    updateProfileField('profilePic', null);
    updateProfileField('profilePicInfo', null);
    setProfilePicSrc('');
  }, [updateProfileField]);

  // PDF download
  const downloadPdf = useCallback(() => {
    App.showPopupNotification('Preparing User Profile PDF for download. Please wait.', 'success');
    api.userProfilePdf(parseInt(userId), (result: any) => {
      const pdfBase64String: string = result.pdfBase64;
      const filename: string = result.pdfFilename;
      if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
        notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
      } else {
        const blob = b64toBlob(pdfBase64String, 'application/pdf');
        saveAs(blob, filename);
      }
    }, () => {
      App.showPopupNotification('Failed to download User Profile PDF.', 'error', 30);
    });
  }, [userId]);

  // Notes image upload handler
  const handleNotesImageUpload = useCallback((insertCallback: (imageUrl: string, alt?: string, width?: string, height?: string, attachmentId?: number | string) => void) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/gif,image/bmp,image/webp';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);

    fileInput.onchange = function () {
      if (!fileInput.files || fileInput.files.length === 0) {
        document.body.removeChild(fileInput);
        return;
      }

      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (_idx: number, attachmentInfo: AttachmentInfo) => {
          editorAttachmentKeysRef.current[attachmentInfo.id] = attachmentInfo.activationKey || '';
        },
        successCallback: (_idx: number, attachment: Attachment) => {
          insertCallback(attachment.signedURL, undefined, undefined, undefined, attachment.id);
          document.body.removeChild(fileInput);
        },
        updateAttachment: () => {},
      });
    };

    fileInput.click();
  }, []);

  // Password modals
  const openChangePasswordModal = useCallback(() => {
    setChangePasswordModal({
      open: true,
      currentPassword: '',
      newPassword1: '',
      newPassword2: '',
    });
  }, []);

  const closeChangePasswordModal = useCallback(() => {
    setChangePasswordModal((prev) => ({ ...prev, open: false }));
  }, []);

  const submitChangePassword = useCallback(() => {
    const data = {
      currentPassword: changePasswordModal.currentPassword,
      newPassword1: changePasswordModal.newPassword1,
      newPassword2: changePasswordModal.newPassword2,
    };
    api.updatePassword(data, () => {
      closeChangePasswordModal();
      App.showPopupNotification('Update Successful', 'success');
    });
  }, [changePasswordModal, closeChangePasswordModal]);

  const openResetPasswordModal = useCallback(() => {
    setResetPasswordModal({ open: true });
  }, []);

  const closeResetPasswordModal = useCallback(() => {
    setResetPasswordModal({ open: false });
  }, []);

  const submitResetPassword = useCallback(() => {
    api.passwordReset(userId, () => {
      closeResetPasswordModal();
      App.showPopupNotification('Password Reset Email Sent', 'success');
    });
  }, [userId, closeResetPasswordModal]);

  const openExportPdfHelp = useCallback(() => {
    setExportPdfHelpOpen(true);
  }, []);

  const closeExportPdfHelp = useCallback(() => {
    setExportPdfHelpOpen(false);
  }, []);

  const exportPdfViaPrint = useCallback(() => {
    setExportPdfHelpOpen(false);
    if (notesEditorRef.current?.printContent) {
      notesEditorRef.current.printContent();
    }
  }, []);

  // ── Render ───────────────────────────────────────────────────────

  if (!userProfile) {
    return (
      <div className={`kst-user-profile-screen ${theme}`}>
        <div className="kst-user-profile-content">
          <div className="kst-loading"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`kst-user-profile-screen ${theme}`}>
      <div className="kst-user-profile-content">
        {/* Profile Header */}
        <div className={`kst-user-profile-header ${theme}`}>
          <div className="kst-user-profile-header-left">
            {/* User Selector Dropdown */}
            {hasViewOtherProfilesAccess ? (
              <div className="kst-user-selector">
                <Dropdownlist
                  value={selectedUserId}
                  items={peopleInCompany}
                  labelKey="name"
                  valueKey="id"
                  placeholder="Select User..."
                  searchable={true}
                  sortItems={true}
                  onChange={(val) => onUserSelect(val as number)}
                />
              </div>
            ) : (
              <div>
                <h3 className="kst-user-profile-title">{userProfile.lastName}, {userProfile.firstName}</h3>
              </div>
            )}
          </div>
          <div className="kst-user-profile-header-right">
            {activeTab === 'profile' && (
              <>
                {!isEditMode && (isCurrentUserProfile || hasEditOtherProfilesAccess) && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--primary" onClick={downloadPdf}>
                    <i className="fa fa-cloud-download"></i>
                    <span className="btn-text">Download PDF</span>
                  </button>
                )}
                {!isEditMode && isCurrentUserProfile && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--primary" onClick={openChangePasswordModal}>
                    <i className="fa fa-key"></i>
                    <span className="btn-text">Change Password</span>
                  </button>
                )}
                {!isEditMode && !isCurrentUserProfile && hasEditOtherProfilesAccess && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--danger" onClick={openResetPasswordModal}>
                    <i className="fa fa-user-secret"></i>
                    <span className="btn-text">Reset Password</span>
                  </button>
                )}
                {!isEditMode && (isCurrentUserProfile || hasEditOtherProfilesAccess) && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--primary" onClick={handleEdit}>
                    <i className="fa fa-edit"></i>
                    <span className="btn-text">Edit</span>
                  </button>
                )}
                {isEditMode && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--success" onClick={handleUpdate}>
                    <i className="fa fa-check"></i>
                    {' '}Update
                  </button>
                )}
                {isEditMode && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--danger" onClick={handleCancel}>
                    <i className="fa fa-ban"></i>
                    {' '}Cancel
                  </button>
                )}
              </>
            )}
            {activeTab === 'preferences' && (
              <>
                {!isPreferencesEditMode && (isCurrentUserProfile || hasEditOtherProfilesAccess) && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--primary" onClick={handlePreferencesEdit}>
                    <i className="fa fa-edit"></i>
                    <span className="btn-text">Edit</span>
                  </button>
                )}
                {isPreferencesEditMode && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--success" onClick={handlePreferencesUpdate}>
                    <i className="fa fa-check"></i>
                    {' '}Update
                  </button>
                )}
                {isPreferencesEditMode && (
                  <button type="button" className="kst-user-profile-btn kst-user-profile-btn--danger" onClick={handlePreferencesCancel}>
                    <i className="fa fa-ban"></i>
                    {' '}Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="kst-user-profile-tabs">
          <ul className="kst-user-profile-tab-list">
            <li className={`kst-user-profile-tab ${activeTab === 'profile' ? 'active' : ''}`}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('profile'); }}>Profile</a>
            </li>
            <li className={`kst-user-profile-tab ${activeTab === 'preferences' ? 'active' : ''}`}>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('preferences'); }}>Preferences</a>
            </li>
          </ul>
        </div>

        {activeTab === 'profile' && (<>
        {/* Profile Picture */}
        <div className="kst-profile-pic-container">
          <div
            className={`kst-profile-pic-wrapper ${isEditMode ? 'cursor-pointer' : ''}`}
            onClick={triggerProfilePicUpload}
          >
            {hasProfilePic ? (
              <img src={profilePicSrc} alt="Profile" className="kst-profile-pic" />
            ) : (
              <div className="kst-letter-avatar" style={{ backgroundColor: avatarColor }}>
                <span className="kst-letter-avatar-text">{userInitials}</span>
              </div>
            )}
            <input
              ref={profilePicInputRef}
              type="file"
              style={{ display: 'none' }}
              accept="image/*"
              onChange={onProfilePicChange}
            />
          </div>
          {hasProfilePic && isEditMode && (
            <div>
              <button type="button" className="kst-remove-pic-btn" onClick={removeProfilePic}>
                REMOVE PICTURE
              </button>
            </div>
          )}
        </div>

        {/* Personal Information Section */}
        <div className={`kst-user-profile-section ${theme}`}>
          <div className="kst-user-profile-section-title">Personal Information</div>
          <div className="kst-user-profile-section-body">
            <div className="kst-user-profile-form-grid">
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Last Name</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.lastName || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('lastName', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">First Name</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.firstName || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('firstName', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Middle Name</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.middleName || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('middleName', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Primary E-Mail</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.primaryEmail || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('primaryEmail', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Secondary E-Mail</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.secondaryEmail || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('secondaryEmail', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Personal Cell</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.personalCell || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('personalCell', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Work Cell</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.workCell || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('workCell', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Home Address</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.locAddress || ''}
                  placeholder="Address / Intersection"
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('locAddress', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Apt. / Suite</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.locAddress2 || ''}
                  placeholder="Apt. / Suite"
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('locAddress2', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Building</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.locName || ''}
                  placeholder="Building"
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('locName', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Contact Section */}
        <div className={`kst-user-profile-section ${theme}`}>
          <div className="kst-user-profile-section-title">Emergency Contact Information</div>
          <div className="kst-user-profile-section-body">
            <div className="kst-user-profile-form-grid">
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Emergency Contact Name</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.emergencyContactName || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('emergencyContactName', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Emergency Contact Phone</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.emergencyContactPhone || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('emergencyContactPhone', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Employee Information Section */}
        <div className={`kst-user-profile-section ${theme}`}>
          <div className="kst-user-profile-section-title">Employee Information</div>
          <div className="kst-user-profile-section-body">
            <div className="kst-user-profile-form-grid">
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">User ID</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.userid || ''}
                  disabled
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Badge #</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.badgeNum || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('badgeNum', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Agency</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.agency || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('agency', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Job Title</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.jobTitle || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('jobTitle', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Call Sign</label>
                {!hasCallSignList ? (
                  <input
                    type="text"
                    className="kst-user-profile-input"
                    value={userProfile.callSign || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateProfileField('callSign', e.target.value)}
                  />
                ) : (
                  <div className={isCallSignInvalid && isEditMode ? 'kst-call-sign-invalid' : ''}>
                    <Dropdownlist
                      value={userProfile.callSign || ''}
                      items={callSignOptions}
                      labelKey="label"
                      valueKey="value"
                      placeholder="-- Select Call Sign --"
                      disabled={!isEditMode}
                      clearable={true}
                      sortItems={false}
                      onChange={(val) => onCallSignSelect(val)}
                    />
                    {isCallSignInvalid && isEditMode && (
                      <div className="kst-call-sign-invalid-warning">
                        <i className="fa fa-exclamation-triangle"></i>
                        Your current call sign is no longer available. Please select a new one before saving.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Employee ID</label>
                <input
                  type="text"
                  className="kst-user-profile-input"
                  value={userProfile.employeeId || ''}
                  disabled={!isEditMode}
                  onChange={(e) => updateProfileField('employeeId', e.target.value)}
                />
              </div>
              <div className="kst-user-profile-form-group">
                <label className="kst-user-profile-label">Hire Date</label>
                <DateTimePicker
                  value={localHireDate}
                  dateOnly={true}
                  disabled={!isEditMode}
                  onChange={(val: Date | null) => setLocalHireDate(val)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notes Section (Restricted Access) */}
        {hasNotesAccess && (
          <div className={`kst-user-profile-section kst-user-profile-notes-section ${theme}`}>
            <div className="kst-user-profile-section-title">Notes - Restricted Access</div>
            <div className="kst-user-profile-section-body">
              <RichTextEditor
                ref={notesEditorRef}
                value={notesContent}
                isReadOnly={!isEditMode}
                height="600px"
                onImageUpload={handleNotesImageUpload}
                toolbarConfig={notesToolbarConfig}
                placeholder=""
                onChange={(html: string) => setNotesContent(html)}
                onExportPdf={openExportPdfHelp}
              />
            </div>
          </div>
        )}
        </>)}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="kst-user-profile-preferences">
            {preferencesFormData == null ? (
              <div className="kst-loading"></div>
            ) : (
              <div className={`kst-user-profile-section ${theme}`}>
                <div className="kst-user-profile-section-title">Mobile (MDT) Preferences</div>
                <div className="kst-user-profile-section-body">
                  <div className="kst-user-profile-form-grid">
                    <div className="kst-user-profile-form-group kst-user-profile-form-group--toggle">
                      <label className="kst-user-profile-label">Open calls in edit mode by default</label>
                      <label className="kst-toggle-switch">
                        <input
                          type="checkbox"
                          checked={preferencesFormData.isMdtCallCardEditModeDefault || false}
                          disabled={!isPreferencesEditMode}
                          onChange={(e) => setPreferencesFormData((prev: any) => ({
                            ...prev,
                            isMdtCallCardEditModeDefault: e.target.checked,
                          }))}
                        />
                        <span className="kst-toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      <ConfirmModal
        open={changePasswordModal.open}
        title="Change Password"
        confirmText="Update Password"
        cancelText="Cancel"
        confirmClass="success"
        autoFocus="none"
        onConfirm={submitChangePassword}
        onCancel={closeChangePasswordModal}
      >
        <div className="kstc-form-fields">
          <div className="kstc-field">
            <label>Current Password</label>
            <input
              type="password"
              className="kstc-input"
              value={changePasswordModal.currentPassword}
              autoComplete="current-password"
              onChange={(e) => setChangePasswordModal((prev) => ({ ...prev, currentPassword: e.target.value }))}
            />
          </div>
          <div className="kstc-field">
            <label>New Password</label>
            <input
              type="password"
              className="kstc-input"
              value={changePasswordModal.newPassword1}
              autoComplete="new-password"
              onChange={(e) => setChangePasswordModal((prev) => ({ ...prev, newPassword1: e.target.value }))}
            />
          </div>
          <div className="kstc-field">
            <label>New Password (repeat)</label>
            <input
              type="password"
              className="kstc-input"
              value={changePasswordModal.newPassword2}
              autoComplete="new-password"
              onChange={(e) => setChangePasswordModal((prev) => ({ ...prev, newPassword2: e.target.value }))}
            />
          </div>
        </div>
      </ConfirmModal>

      {/* Reset Password Modal */}
      <ConfirmModal
        open={resetPasswordModal.open}
        title="Reset Password"
        message="Click Reset Password below to send a password reset email to this user."
        confirmText="Reset Password"
        cancelText="Cancel"
        confirmClass="danger"
        onConfirm={submitResetPassword}
        onCancel={closeResetPasswordModal}
      />

      {/* Export to PDF Help Modal */}
      <ConfirmModal
        open={exportPdfHelpOpen}
        title="Export to PDF"
        message="To save as a PDF, use the Print button and select 'Save as PDF' (or 'Microsoft Print to PDF') as the destination in the print dialog."
        confirmText="Open Print Dialog"
        cancelText="Close"
        confirmClass="primary"
        onConfirm={exportPdfViaPrint}
        onCancel={closeExportPdfHelp}
      />
    </div>
  );
}
